import React, { MouseEvent, useEffect, useState } from 'react';
import { makeStyles } from '@mui/styles';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import ReportProblemRoundedIcon from '@mui/icons-material/ReportProblemRounded';
import { CircularProgress, Typography, Button } from '@mui/material';
import { Box } from '@mui/system';
import Popover from '@mui/material/Popover';
import VisualIndicator, { IVisualIndicator } from './VisualIndicator';
import Colors from '../../utils/color';
import { CheckStatus } from '../../redux/features/healthStatusCheck/healthStatusCheck.constant';
import { useAppSelector, useAppDispatch } from '../../app/hooks';
import { selectHealthStatusCheckState } from '../../redux/features/healthStatusCheck/healthStatusCheck.selector';
import {
  checkHealthStatus,
  reportLogsToPagerDuty,
  resetHealthStatusState,
} from '../../redux/features/healthStatusCheck/healthStatusCheck.slice';
import { selectActiveRestaurantCode } from '../../selectors/restaurant';
import theme from '../../utils/theme';
import { ISystemsStatus } from '../../redux/features/healthStatusCheck/healthStatusCheck.props';
import { RESET_HEALTH_STATUS_ONCLOSE_DELAY } from '../../constants';
import { selectIsPosmonDisconnected } from '../../selectors/order';

const useStyles = makeStyles({
  container: {
    padding: `${theme.spacing(3)}`,
  },
  bodyContainer: {
    margin: `${theme.spacing(2)} 0`,
  },
  lineItemsWrapper: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  btnContainer: {
    marginTop: `${theme.spacing(2)}`,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    flexDirection: 'column',
  },
  btn: {
    width: '100%',
    margin: `${theme.spacing(1)} 0 !important`,
  },
  boldFont: {
    fontWeight: 'bold !important',
  },
  italicFontStyle: {
    fontStyle: 'italic !important',
    fontSize: '0.625rem !important',
  },
});

export enum SendReportStatus {
  notAplicable = 'notAplicable',
  sending = 'sending',
  sent = 'sent',
  failed = 'failed',
}

/**
 * Runs a health check by itself whenever the POS monitor link drops.
 *
 * HITL-1702: lifted out of HealthStatusCheck so the 7.0 System icon — which
 * replaces this icon in the header — keeps the same automatic check. Exactly
 * one of the two is mounted for any flag combination, so it never runs twice.
 */
export const useHealthCheckOnPosmonDisconnect = () => {
  const dispatch = useAppDispatch();
  const isPosmonDisconnected = useAppSelector(selectIsPosmonDisconnected);
  useEffect(() => {
    if (isPosmonDisconnected) dispatch(checkHealthStatus());
  }, [isPosmonDisconnected]);
};

/**
 * Report-to-PagerDuty and copy actions for the health check result. Shared by
 * the legacy health icon and the 7.0 System panel (HITL-1702).
 */
export const useHealthStatusReport = () => {
  const dispatch = useAppDispatch();
  const { isHealthy, healthStatusCheckContent, generatedTime } = useAppSelector(
    selectHealthStatusCheckState
  );
  const restaurantCode = useAppSelector(selectActiveRestaurantCode);
  const {
    body: healthStatusCheckContentBody,
    heading: healthStatusCheckContentHeading,
  } = healthStatusCheckContent;

  const [sendReportStatus, setSendReportStatus] = useState(
    SendReportStatus.notAplicable
  );

  const onReportClick = async () => {
    try {
      setSendReportStatus(SendReportStatus.sending);
      await dispatch(
        reportLogsToPagerDuty({ restaurantCode: restaurantCode || '' })
      );
      setSendReportStatus(SendReportStatus.sent);
    } catch (error) {
      setSendReportStatus(SendReportStatus.failed);
    }
  };

  const formSystemCheckContentBody = (systemsStatus: ISystemsStatus[]) =>
    systemsStatus.map(({ name, status }) => `${name} ${status}`);

  const onCopyClick = () => {
    const text = [
      healthStatusCheckContentHeading,
      ...(isHealthy
        ? [healthStatusCheckContentBody]
        : formSystemCheckContentBody(
            healthStatusCheckContentBody as ISystemsStatus[]
          )),
      generatedTime,
    ].join('\n');

    navigator.clipboard.writeText(text);
  };

  return { sendReportStatus, setSendReportStatus, onReportClick, onCopyClick };
};

interface HealthStatusCheckBodyProps {
  onClose: () => void;
  /**
   * HITL-1702: shown inside the 7.0 System panel, which titles itself with the
   * restaurant and lists the result — and each unhealthy system — as its own
   * rows. Embedded, this drops the heading, the result list and the space they
   * took, leaving the generated time and the Report / Copy / Close buttons.
   */
  embedded?: boolean;
  sendReportStatus: SendReportStatus;
  onReportClick: () => void;
  onCopyClick: () => void;
}

/**
 * The health check result — heading, each unhealthy system, the generated time
 * and the Report / Copy / Close buttons. The legacy icon shows it in its own
 * popover; the 7.0 System panel shows it beneath the live rows (HITL-1702).
 * Uses the same sheet as the icon, so the legacy markup is unchanged.
 */
export const HealthStatusCheckBody = ({
  onClose,
  embedded = false,
  sendReportStatus,
  onReportClick,
  onCopyClick,
}: HealthStatusCheckBodyProps) => {
  const {
    container,
    bodyContainer,
    lineItemsWrapper,
    btnContainer,
    btn,
    boldFont,
    italicFontStyle,
  } = useStyles();
  const { isHealthy, healthStatusCheckContent, generatedTime } = useAppSelector(
    selectHealthStatusCheckState
  );
  const {
    body: healthStatusCheckContentBody,
    heading: healthStatusCheckContentHeading,
  } = healthStatusCheckContent;

  const reportStatusButtonLabel: { [key in SendReportStatus]: string } = {
    [SendReportStatus.notAplicable]: 'Report on Pager Duty',
    [SendReportStatus.sending]: 'Reporting...',
    [SendReportStatus.failed]: 'Failed, try again',
    [SendReportStatus.sent]: 'Report submitted',
  };

  return (
    <div
      className={container}
      // Inline and only when embedded, so the legacy popover's markup is
      // unchanged (adding a makeStyles rule would renumber JSS classes).
      style={embedded ? { paddingTop: 0 } : undefined}
    >
      {!embedded && (
        <Typography variant="body2" className={boldFont} color="primary">
          {healthStatusCheckContentHeading}
        </Typography>
      )}
      {!embedded && (
        <div className={bodyContainer}>
          {isHealthy ? (
            <Typography variant="body2" gutterBottom>
              {healthStatusCheckContentBody}
            </Typography>
          ) : (
            <>
              {Array.isArray(healthStatusCheckContentBody) &&
                (healthStatusCheckContentBody as ISystemsStatus[])?.map(
                  ({ name, status }) => (
                    <div key={name} className={lineItemsWrapper}>
                      <Typography variant="body2" gutterBottom>
                        {name}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="error.dark"
                        className={boldFont}
                        gutterBottom
                      >
                        {status}
                      </Typography>
                    </div>
                  )
                )}
            </>
          )}
        </div>
      )}
      <Typography variant="body2" className={italicFontStyle}>
        {generatedTime}
      </Typography>
      <Box
        className={btnContainer}
        // Embedded, the buttons sit closer to the generated time: the group's
        // own 16px goes, each button's 8px margin stays (HITL-1702).
        style={embedded ? { marginTop: 0 } : undefined}
      >
        {!isHealthy && (
          <Button
            className={btn}
            disabled={[
              SendReportStatus.sending,
              SendReportStatus.sent,
            ].includes(sendReportStatus)}
            variant="contained"
            onClick={onReportClick}
            color="secondary"
          >
            {reportStatusButtonLabel[sendReportStatus]}
          </Button>
        )}
        <Button
          className={btn}
          variant="contained"
          onClick={onCopyClick}
          color="secondary"
        >
          Copy To Clipboard
        </Button>
        <Button
          className={btn}
          variant="outlined"
          onClick={onClose}
          color="primary"
        >
          Close
        </Button>
      </Box>
    </div>
  );
};

const HealthStatusCheck = () => {
  // Attaches this file's sheet before VisualIndicator's, as it did before the
  // popover body moved into HealthStatusCheckBody (HITL-1702). JSS numbers class
  // names in attach order, so dropping this renumbers the legacy markup.
  useStyles();
  const dispatch = useAppDispatch();
  const { isHealthy, checkStatus } = useAppSelector(
    selectHealthStatusCheckState
  );
  const restaurantCode = useAppSelector(selectActiveRestaurantCode);

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const { sendReportStatus, setSendReportStatus, onReportClick, onCopyClick } =
    useHealthStatusReport();

  useHealthCheckOnPosmonDisconnect();

  const onHealthStatusCheckClick = (event: MouseEvent<HTMLButtonElement>) => {
    if (checkStatus === CheckStatus.completed && !isHealthy) {
      setAnchorEl(event.currentTarget);
    } else if (!checkStatus) {
      dispatch(checkHealthStatus());
    }
  };

  const visualIndicatorProps = ((): IVisualIndicator => {
    let visualIndicatorProps: IVisualIndicator = {
      onClick: onHealthStatusCheckClick,
      id: 'health-status-check',
      IconComponent: ErrorOutlineIcon,
    };
    if (!restaurantCode) {
      return { ...visualIndicatorProps };
    } else if (checkStatus === CheckStatus.completed && !isHealthy) {
      return {
        ...visualIndicatorProps,
        IconComponent: ReportProblemRoundedIcon,
        customizedColor: Colors.texasRose,
      };
    } else if (checkStatus === CheckStatus.completed && isHealthy) {
      return {
        ...visualIndicatorProps,
        IconComponent: CheckCircleRoundedIcon,
        customizedColor: Colors.brightGreen,
      };
    } else if (checkStatus === CheckStatus.pending) {
      return {
        ...visualIndicatorProps,
        IconComponent: CircularProgress,
        customizedColor: Colors.white,
      };
    }
    return { ...visualIndicatorProps, customizedColor: Colors.white };
  })();

  const open = Boolean(anchorEl);
  const id = open ? 'health-status-check-popover' : undefined;

  const handleClose = () => {
    setAnchorEl(null);
    setTimeout(() => {
      dispatch(resetHealthStatusState());
      setSendReportStatus(SendReportStatus.notAplicable);
    }, RESET_HEALTH_STATUS_ONCLOSE_DELAY); // Reset the state with a delay for UI purpose
  };

  return (
    <>
      <VisualIndicator {...visualIndicatorProps} />
      <Popover
        id={id}
        open={open}
        onClose={handleClose}
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: -260,
        }}
      >
        <HealthStatusCheckBody
          onClose={handleClose}
          sendReportStatus={sendReportStatus}
          onReportClick={onReportClick}
          onCopyClick={onCopyClick}
        />
      </Popover>
    </>
  );
};

export default HealthStatusCheck;
